import React, { useCallback, useMemo } from 'react';

import {
  AlignItemsStyleOptions,
  Box,
  Inline,
  PaddingSize,
} from '@styleseat/ui';
import {
  TouchableWithoutFeedback,
  StyleProp,
  TextStyle,
} from 'react-native';
import { colors } from '@styleseat/brand';
import { PublicProvider } from '../../../../../api/Providers';
import {
  BodyText,
  Button,
  HeaderText,
  Stack,
  TextFormField,
} from '../../../../ui';
import AvatarIcon from '../../../../AvatarIcon';
import Image from '../../../../shared/Image';
import Currency from '../../../../../modules/Currency';
import { useTip } from '../../../../consumer/tipping/hooks/useTip';
import { ITipOption } from '../../../../../modules/consumer/appointments/tipping';
import { ProAppointment } from '../../../../../api/Providers/Appointments';

const Logo = require('../../../../../../images/styleseat-logo-black.svg');

export type ClientTipSelectionStepProps = {
  appointment: Pick<ProAppointment, 'cost'>, // use this to calculate tip values
  provider: Pick<PublicProvider, 'name' | 'profile_photo'>;
  appointmentCost: number; // use this to display Appointment cost
  onConfirmTip: (selectedTipAmount: number) => void;
};

const ClientTipSelectionStep: React.FC<ClientTipSelectionStepProps> = ({
  appointment,
  provider,
  appointmentCost,
  onConfirmTip,
}) => {
  const avatarUrl = `${provider.profile_photo}_90x90.jpg`;
  const costToCalculateTip = Number.parseFloat(appointment.cost) || 0;
  const tipOptions: ITipOption[] = useMemo(() => [
    {
      amount: costToCalculateTip * 0.2,
      percentage: 20,
      is_custom: false,
      is_default: true,
    },
    {
      amount: costToCalculateTip * 0.25,
      percentage: 25,
      is_custom: false,
      is_default: false,
    },
    {
      amount: costToCalculateTip * 0.3,
      percentage: 30,
      is_custom: false,
      is_default: false,
    },
    {
      amount: 0,
      percentage: undefined,
      is_custom: true,
      is_default: false,
    },
  ], [costToCalculateTip]);

  const handleSubmitTip = ({ amount }) => {
    onConfirmTip(amount);
  };
  const {
    customTip,
    onAlreadyTipped,
    onCustomTipChange,
    onCustomTipFocus,
    onSelectTip,
    onSubmitTip,
    tipAmount,
    tipError,
    tipOption,
  } = useTip(handleSubmitTip);

  React.useEffect(() => {
    const [selectedOption] = tipOptions.filter(o => o.is_default);
    onSelectTip(selectedOption ?? tipOptions[0]);
  }, []);

  const handleSelectTip = useCallback((selectedOption: ITipOption) => {
    if (selectedOption.is_custom) {
      onCustomTipFocus();
    } else {
      onSelectTip(selectedOption);
    }
  }, [tipOptions]);

  return (
    <Stack flex={1} alignItems="stretch" testID="client-tip-selection-step-component">
      <Box>
        <Stack
          flex={1}
          space={20}
          alignItems="center"
          paddingY={16}
        >
          <Stack
            alignItems="center"
            paddingY={10}
            borderBottomColor={colors.lightGrey}
            borderBottomWidth={1}
            borderStyle="solid"
            style={{ width: '100%' }}
          >
            <Image
              source={Logo}
              style={{
                width: 72,
                height: 38,
              }}
            />
          </Stack>
          <Inline
            alignItems="center"
            space={8}
            color={colors.superLight}
            borderRadius={100}
            paddingX={16}
            paddingY={8}
          >
            <AvatarIcon name={provider.name} icon={avatarUrl} sizeClass="extra-xx-small" />
            <BodyText>{provider.name}</BodyText>
          </Inline>
          <Stack space={8} alignItems="center">
            <HeaderText testID="client-tip-selection-appointment-cost">
              {Currency.format(appointmentCost + tipAmount)}
            </HeaderText>
            <BodyText
              appearance="normal"
              weight="thin"
              color={colors.metal}
              testID="client-tip-selection-appointment-cost-with-tip"
            >
              {Currency.format(appointmentCost)} + {Currency.format(tipAmount)} tip
            </BodyText>
          </Stack>
        </Stack>
      </Box>
      <Box
        alignItems="stretch"
        justifyContent="center"
      >
        <Stack paddingX={28} space={10}>
          {tipOptions.map(opt => {
            const id = opt.percentage ?? 'custom';
            const label = opt.is_custom ? 'Custom' : `${opt.percentage}%`;
            const isSelected = tipOption === 'custom' ? opt.is_custom : tipOption?.percentage === id;
            const paddingSize: PaddingSize = opt.is_custom ? 20 : 10;

            return (
              <TouchableWithoutFeedback
                key={label}
                onPress={() => handleSelectTip(opt)}
                testID={`client-tip-selection-${id}-touchable`}
              >
                <Stack
                  borderWidth={1}
                  borderStyle="solid"
                  borderColor={isSelected ? colors.slate : colors.silver}
                  borderRadius={8}
                  alignItems={'center' as AlignItemsStyleOptions}
                  padding={paddingSize}
                  color={isSelected ? colors.chill : undefined}
                >
                  <BodyText
                    weight="bold"
                    color={isSelected ? colors.slate : undefined}
                  >
                    {label}
                  </BodyText>
                  {!opt.is_custom && (
                    <BodyText
                      appearance="medium"
                      color={colors.metal}
                      testID={`client-tip-selection-${id}-percent`}
                    >
                      {Currency.format(costToCalculateTip * (opt.percentage / 100))}
                    </BodyText>
                  )}
                </Stack>
              </TouchableWithoutFeedback>
            );
          })}
          {tipOption === 'custom' && (
            <Stack space={16}>
              <TextFormField
                type="text"
                inputmode="decimal"
                appearance="bordered"
                autoFocus
                label="Tip Amount"
                value={customTip && customTip !== '0' ? customTip : ''}
                onChangeText={onCustomTipChange}
                onFocus={onCustomTipFocus}
                placeholder="$0.00"
                padding={0}
                testID="client-tip-selection-customamount-input"
                style={{
                  textAlign: 'center',
                } as StyleProp<TextStyle>}
              />
            </Stack>
          )}
        </Stack>
        {!!tipError
          && (
            <BodyText
              textAlign="center"
              color={colors.red}
              testID="client-tip-selection-error-message"
            >
              {tipError}
            </BodyText>
          )}
      </Box>
      <Stack
        space={24}
        padding={28}
        borderTopColor={colors.lightGrey}
        borderTopWidth={1}
        borderStyle="solid"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          maxWidth: '100%',
          minWidth: '100%',
        }}
      >
        <Button
          appearance="primary-neue"
          onClick={onSubmitTip}
          disabled={!!tipError}
          testID="client-tip-selection-step-submit"
        >
          Confirm Tip
        </Button>
        <Button
          appearance="link-lowercase"
          onClick={onAlreadyTipped}
          size="medium"
          testID="client-tip-selection-cashtip-button"
        >
          I&rsquo;ll Tip in Cash
        </Button>
      </Stack>
    </Stack>
  );
};

export default ClientTipSelectionStep;
